import { motion } from "framer-motion";
import { ArrowDown, Phone, MapPin } from "lucide-react";

const ease = [0.16, 1, 0.3, 1];

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.9, delay, ease }}
    className={className}
  >
    {children}
  </motion.div>
);

export const HowTo = ({ c, onSignUp }) => {
  const steps = c?.howto_steps || [];
  if (!steps.length) return null;

  const scrollToSignup = () => {
    const el = document.getElementById("signup");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="how-to"
      data-testid="howto-section"
      className="bg-[#0E6B3A] py-24 md:py-36 overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <Reveal className="max-w-4xl">
          <span className="text-xs uppercase tracking-[0.32em] text-[#FBF7F0]/70">
            {c?.howto_eyebrow}
          </span>
          <h2 className="mt-6 font-serif font-light text-4xl sm:text-5xl md:text-6xl leading-[1.02] tracking-tight text-[#FBF7F0]">
            {c?.howto_title}
          </h2>
        </Reveal>

        <div className="mt-16 md:mt-24 grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-10">
          {steps.map((step, i) => (
            <Reveal key={i} delay={0.12 * i}>
              <div data-testid={`howto-step-${i + 1}`}>
                <div className="font-serif font-light text-7xl md:text-8xl leading-none text-[#FBF7F0] border-b border-[#FBF7F0]/25 pb-6">
                  {i + 1}
                </div>
                <h3 className="mt-6 font-serif text-3xl md:text-4xl leading-tight tracking-tight text-[#FBF7F0]">
                  {step.title}
                </h3>
                <p className="mt-4 text-[#FBF7F0]/80 text-base md:text-lg leading-relaxed">
                  {step.body}
                </p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.3} className="mt-16 md:mt-24 flex flex-col sm:flex-row sm:items-center gap-8">
          <button
            data-testid="howto-signup-btn"
            onClick={onSignUp || scrollToSignup}
            className="group w-full sm:w-fit inline-flex items-center justify-center rounded-full bg-[#FBF7F0] text-[#0E6B3A] px-6 sm:px-12 py-5 sm:py-6 text-base sm:text-lg font-bold tracking-wide transition-transform duration-300 hover:scale-[1.04]"
          >
            {c?.hero_cta || "Click here to choose your shift"}
            <ArrowDown className="inline-block ml-2 w-5 h-5 transition-transform duration-300 group-hover:translate-y-1" strokeWidth={2} />
          </button>
          <div className="text-[#FBF7F0]/80 text-base md:text-lg max-w-md">
            <span data-testid="howto-note">{c?.howto_note}</span>{" "}
            <a
              href={`tel:${String(c?.contact_phone || "").replace(/\s+/g, "")}`}
              data-testid="howto-phone"
              className="inline-flex items-center gap-2 font-serif text-2xl md:text-3xl text-[#FBF7F0] hover:underline underline-offset-4 mt-1"
            >
              <Phone className="w-5 h-5" strokeWidth={1.6} />
              {c?.contact_phone}
            </a>
          </div>
        </Reveal>

        <Reveal delay={0.35} className="mt-12 md:mt-16">
          <div
            data-testid="onday-panel"
            className="rounded-2xl bg-[#FBF7F0] p-8 md:p-10 flex flex-col md:flex-row md:items-center gap-6 md:gap-10 max-w-4xl"
          >
            <div className="shrink-0 w-14 h-14 rounded-full bg-[#8E1226] flex items-center justify-center">
              <MapPin className="w-6 h-6 text-[#FBF7F0]" strokeWidth={1.6} />
            </div>
            <div>
              <h3 className="font-serif text-3xl md:text-4xl leading-tight tracking-tight text-[#8E1226]">
                {c?.onday_title}
              </h3>
              <p className="mt-3 text-[#6D726A] text-base md:text-lg leading-relaxed max-w-xl">
                {c?.onday_body}
              </p>
              <p className="mt-4 text-[#8E1226] font-medium text-base md:text-lg" data-testid="onday-address">
                {c?.contact_address}
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
